import React, { useState } from 'react';

export interface RecurrenceRule {
  frequency: 'daily' | 'weekly' | 'monthly' | 'custom';
  interval: number;
  daysOfWeek?: number[]; // 0 = Sunday, 6 = Saturday
  dayOfMonth?: number;
  skipWeekends?: boolean;
}

interface RecurrenceSelectorProps {
  value: RecurrenceRule | null;
  onChange: (rule: RecurrenceRule | null) => void;
  endDate: string | null;
  onEndDateChange: (date: string | null) => void;
}

const RecurrenceSelector: React.FC<RecurrenceSelectorProps> = ({
  value,
  onChange,
  endDate,
  onEndDateChange,
}) => {
  const [isRecurring, setIsRecurring] = useState(!!value);

  const handleToggle = (enabled: boolean) => {
    setIsRecurring(enabled);
    if (!enabled) {
      onChange(null);
      onEndDateChange(null);
    } else {
      // Set default rule
      onChange({
        frequency: 'weekly',
        interval: 1,
        skipWeekends: false,
      });
    }
  };

  const handleFrequencyChange = (frequency: RecurrenceRule['frequency']) => {
    const newRule: RecurrenceRule = {
      ...value,
      frequency,
      interval: value?.interval || 1,
      skipWeekends: value?.skipWeekends || false,
    };

    // Set defaults based on frequency
    if (frequency === 'weekly') {
      newRule.daysOfWeek = value?.daysOfWeek || [1, 2, 3, 4, 5]; // Mon-Fri
    } else if (frequency === 'monthly') {
      newRule.dayOfMonth = value?.dayOfMonth || 1;
    }

    onChange(newRule);
  };

  const handleIntervalChange = (interval: number) => {
    if (value) {
      onChange({ ...value, interval });
    }
  };

  const handleDayToggle = (day: number) => {
    if (!value || value.frequency !== 'weekly') return;

    const days = value.daysOfWeek || [];
    const newDays = days.includes(day)
      ? days.filter(d => d !== day)
      : [...days, day].sort();

    onChange({ ...value, daysOfWeek: newDays });
  };

  const handleDayOfMonthChange = (day: number) => {
    if (value && value.frequency === 'monthly') {
      onChange({ ...value, dayOfMonth: day });
    }
  };

  const handleSkipWeekendsChange = (skip: boolean) => {
    if (value) {
      onChange({ ...value, skipWeekends: skip });
    }
  };

  const weekDays = [
    { label: 'Sun', value: 0 },
    { label: 'Mon', value: 1 },
    { label: 'Tue', value: 2 },
    { label: 'Wed', value: 3 },
    { label: 'Thu', value: 4 },
    { label: 'Fri', value: 5 },
    { label: 'Sat', value: 6 },
  ];

  return (
    <div className="space-y-4">
      {/* Toggle Recurring */}
      <div className="flex items-center gap-3">
        <input
          type="checkbox"
          id="is-recurring"
          checked={isRecurring}
          onChange={(e) => handleToggle(e.target.checked)}
          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
        <label htmlFor="is-recurring" className="text-sm font-medium text-gray-900">
          Make this a recurring task
        </label>
      </div>

      {/* Recurrence Options */}
      {isRecurring && value && (
        <div className="space-y-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
          {/* Frequency */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Repeat
            </label>
            <select
              value={value.frequency}
              onChange={(e) => handleFrequencyChange(e.target.value as RecurrenceRule['frequency'])}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>

          {/* Interval */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Every
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="1"
                max="365"
                value={value.interval}
                onChange={(e) => handleIntervalChange(parseInt(e.target.value) || 1)}
                className="w-20 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">
                {value.frequency === 'daily' && 'day(s)'}
                {value.frequency === 'weekly' && 'week(s)'}
                {value.frequency === 'monthly' && 'month(s)'}
              </span>
            </div>
          </div>

          {/* Days of Week (for weekly) */}
          {value.frequency === 'weekly' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                On these days
              </label>
              <div className="flex gap-2">
                {weekDays.map((day) => (
                  <button
                    key={day.value}
                    onClick={() => handleDayToggle(day.value)}
                    className={`flex h-10 w-10 items-center justify-center rounded-full text-xs font-medium transition-colors ${
                      value.daysOfWeek?.includes(day.value)
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {day.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Day of Month (for monthly) */}
          {value.frequency === 'monthly' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                On day
              </label>
              <input
                type="number"
                min="1"
                max="31"
                value={value.dayOfMonth || 1}
                onChange={(e) => handleDayOfMonthChange(parseInt(e.target.value) || 1)}
                className="w-20 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-600">
                of the month
              </span>
            </div>
          )}

          {/* Skip Weekends */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="skip-weekends"
              checked={value.skipWeekends || false}
              onChange={(e) => handleSkipWeekendsChange(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="skip-weekends" className="text-sm text-gray-700">
              Skip weekends
            </label>
          </div>

          {/* End Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              End date (optional)
            </label>
            <input
              type="date"
              value={endDate || ''}
              onChange={(e) => onEndDateChange(e.target.value || null)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          {/* Preview */}
          <div className="rounded-md bg-blue-50 border border-blue-200 p-3">
            <p className="text-xs font-medium text-blue-900 mb-1">Summary</p>
            <p className="text-sm text-blue-800">
              Repeats every {value.interval} {value.frequency === 'daily' && 'day(s)'}
              {value.frequency === 'weekly' && 'week(s)'}
              {value.frequency === 'monthly' && 'month(s)'}
              {value.frequency === 'weekly' && value.daysOfWeek && value.daysOfWeek.length > 0 && (
                <> on {value.daysOfWeek.map(d => weekDays[d].label).join(', ')}</>
              )}
              {value.frequency === 'monthly' && value.dayOfMonth && (
                <> on day {value.dayOfMonth}</>
              )}
              {value.skipWeekends && ' (skipping weekends)'}
              {endDate && <> until {new Date(endDate).toLocaleDateString()}</>}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecurrenceSelector;
